/**
 * Circadian Phase Z-Score — measures deviation of sleep timing from personal baseline.
 *
 * Research: Circadian phase deviation is the single most predictive wearable feature
 * for depression, with AUC 0.80 (Cho et al., 2024). More predictive than absolute
 * sleep duration, HRV, or step count.
 *
 * All time math uses circular statistics to correctly handle midnight wrap-around.
 */

const TWO_PI = 2 * Math.PI;

// ── Helpers ──────────────────────────────────────────────────────────────────

/** Convert hours (0–24) to radians for circular statistics. */
function hoursToRad(h: number): number {
  return ((h % 24) * TWO_PI) / 24;
}

/** Convert radians back to hours (0–24). Normalizes to [0, 24). */
function radToHours(r: number): number {
  let h = ((r * 24) / TWO_PI) % 24;
  if (h < 0) h += 24;
  // Clamp near-24 to 0 for clean midnight handling
  if (h >= 23.9999) h = 0;
  return h;
}

/**
 * Shortest arc distance between two times on a 24-hour clock.
 * e.g., 23:00 and 01:00 are 2 hours apart, not 22.
 */
export function circularDistance(hour1: number, hour2: number): number {
  const diff = Math.abs((hour1 % 24) - (hour2 % 24));
  return Math.min(diff, 24 - diff);
}

/**
 * Signed circular distance: positive if `target` is clockwise-later than `ref`.
 * e.g., signedCircularDistance(0.5, 23.5) → +1.0 (later)
 *       signedCircularDistance(23.5, 0.5) → -1.0 (earlier)
 */
function signedCircularDistance(target: number, ref: number): number {
  let diff = ((target % 24) - (ref % 24) + 24) % 24;
  if (diff > 12) diff -= 24;
  return diff;
}

// ── Exported Calculations ────────────────────────────────────────────────────

/**
 * Calculate the circadian midpoint from sleep onset and wake time.
 * The midpoint is the halfway point of the sleep episode on a 24-hour clock.
 * @param sleepOnset - Time the user fell asleep (Date or ISO string)
 * @param wakeTime - Time the user woke up (Date or ISO string)
 * @returns Midpoint in hours (0–24 scale, e.g., 3.5 = 3:30 AM)
 */
export function calcCircadianMidpoint(sleepOnset: Date | string, wakeTime: Date | string): number {
  const onset = typeof sleepOnset === 'string' ? new Date(sleepOnset) : sleepOnset;
  const wake = typeof wakeTime === 'string' ? new Date(wakeTime) : wakeTime;

  const onsetH = onset.getHours() + onset.getMinutes() / 60;
  const wakeH = wake.getHours() + wake.getMinutes() / 60;

  // Duration in hours (handles midnight crossing)
  let duration = wakeH - onsetH;
  if (duration <= 0) duration += 24;

  // Midpoint = onset + half the duration, wrapped to 0–24
  const mid = (onsetH + duration / 2) % 24;
  return mid < 0 ? mid + 24 : mid;
}

/**
 * Compute the circular mean of a set of hours (0–24).
 * Uses angular mean via sin/cos to correctly handle midnight wrap.
 */
export function circularMean(hours: number[]): number {
  if (hours.length === 0) return 0;
  const sinSum = hours.reduce((s, h) => s + Math.sin(hoursToRad(h)), 0);
  const cosSum = hours.reduce((s, h) => s + Math.cos(hoursToRad(h)), 0);
  return radToHours(Math.atan2(sinSum / hours.length, cosSum / hours.length));
}

/**
 * Compute the circular standard deviation of a set of hours.
 * Uses the formula: σ = √(-2 · ln(R)) converted to hours,
 * where R is the mean resultant length.
 */
export function circularSD(hours: number[]): number {
  if (hours.length < 2) return 0;
  const n = hours.length;
  const sinSum = hours.reduce((s, h) => s + Math.sin(hoursToRad(h)), 0);
  const cosSum = hours.reduce((s, h) => s + Math.cos(hoursToRad(h)), 0);
  const R = Math.sqrt((sinSum / n) ** 2 + (cosSum / n) ** 2);
  // Clamp R to avoid log(0) or negative values from floating-point error
  const Rc = Math.min(Math.max(R, 0.0001), 1);
  const sdRad = Math.sqrt(-2 * Math.log(Rc));
  return (sdRad * 24) / TWO_PI; // convert radians to hours
}

/**
 * Calculate the personal baseline circadian midpoint from historical data.
 * Uses a 14-day rolling circular median to resist outlier influence.
 * @param sleepHistory - Array of sleep episodes with onset and wake times
 * @returns {{ median: number, sd: number, midpoints: number[] }} Baseline stats in hours
 */
export function calcBaselineMidpoint(
  sleepHistory: Array<{ sleepOnset: Date | string; wakeTime: Date | string }>
): { median: number; sd: number; midpoints: number[] } | null {
  if (sleepHistory.length < 3) return null;

  const midpoints = sleepHistory.map(s => calcCircadianMidpoint(s.sleepOnset, s.wakeTime));
  const mean = circularMean(midpoints);

  // Circular median: sort by distance from circular mean, take the middle value
  const sorted = [...midpoints].sort(
    (a, b) => circularDistance(a, mean) - circularDistance(b, mean)
  );
  const median = sorted[Math.floor(sorted.length / 2)];
  const sd = circularSD(midpoints);

  return { median, sd, midpoints };
}

/**
 * Calculate circadian phase Z-score: how far tonight's midpoint deviates
 * from the personal 14-day baseline, normalized by the standard deviation.
 *
 * Positive = later than usual, Negative = earlier than usual.
 *
 * @param todayMidpoint - Tonight's midpoint in hours
 * @param baselineMedian - 14-day median midpoint in hours
 * @param baselineSD - 14-day standard deviation in hours
 * @returns Z-score (0 if SD is 0 to avoid Infinity)
 */
export function calcCircadianZScore(
  todayMidpoint: number,
  baselineMedian: number,
  baselineSD: number
): number {
  if (baselineSD <= 0) return 0;
  const dist = signedCircularDistance(todayMidpoint, baselineMedian);
  return dist / baselineSD;
}

export type CircadianSeverity = 'stable' | 'mild' | 'moderate' | 'significant';

/**
 * Interpret the Z-score into a clinical category.
 * |Z| < 0.5  → "Stable" (green)
 * |Z| 0.5–1.0 → "Mild shift" (amber)
 * |Z| 1.0–2.0 → "Moderate shift" (orange)
 * |Z| > 2.0  → "Significant shift" (red)
 */
export function interpretCircadianZ(zScore: number): {
  label: string;
  severity: CircadianSeverity;
  color: string;
} {
  const abs = Math.abs(zScore);
  const direction = zScore >= 0 ? 'later' : 'earlier';

  if (abs < 0.5) return { label: 'Stable', severity: 'stable', color: '#22C55E' };
  if (abs < 1.0) return { label: `Mild shift — ${direction} than usual`, severity: 'mild', color: '#F59E0B' };
  if (abs < 2.0) return { label: `Moderate shift — ${direction} than usual`, severity: 'moderate', color: '#F97316' };
  return { label: `Significant shift — ${direction} than usual`, severity: 'significant', color: '#EF4444' };
}

/**
 * Normalize circadian Z-score to a 0–100 pillar input score.
 * |Z| < 0.5 → 100, 0.5–1.0 → 75, 1.0–2.0 → 50, > 2.0 → 25
 */
export function normalizeCircadianZ(zScore: number): number {
  const abs = Math.abs(zScore);
  if (abs < 0.5) return 100;
  if (abs < 1.0) return 75;
  if (abs < 2.0) return 50;
  return 25;
}

/**
 * Format an hour value (0–24) as a human-readable time string.
 * e.g., 3.5 → "3:30 AM", 14.25 → "2:15 PM"
 */
export function formatHourAsTime(hour: number): string {
  const h = ((Math.floor(hour) % 24) + 24) % 24;
  const m = Math.round((hour - Math.floor(hour)) * 60);
  const period = h >= 12 ? 'PM' : 'AM';
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${h12}:${String(m).padStart(2, '0')} ${period}`;
}
